/**
 * Validations des steps du checkout adhésion /a/[slug] :
 *  - canGoFromStep1 (au moins 1 sélection + flexible price valide)
 *  - canGoFromStep1Single (⚠️ PLUS UTILISÉ depuis que l'étape « Participants »
 *    est toujours affichée : les champs personnalisés s'y saisissent, plus à
 *    l'étape 1. Conservé le temps de vérifier qu'aucun autre écran ne s'en
 *    sert.)
 *  - allParticipantsCustomFieldsValid (custom fields requis tous participants)
 *  - coordsValid (formulaire payeur complet)
 */
import type { MembershipPlanPublic, MembershipCustomField } from "~/types/membership";

interface ParticipantLocal {
  planId: string;
  firstName: string;
  lastName: string;
  email: string;
  customFieldValues: Record<string, unknown>;
}

function isCustomFieldValueValid(val: unknown, fieldType: string): boolean {
  if (val === undefined || val === null || val === "") return false;
  // Choix multiple : une liste vide n'est pas une réponse (elle passait le
  // contrôle « obligatoire »).
  if (Array.isArray(val)) return val.length > 0;
  if (fieldType === "file") {
    return typeof val === "object" && val !== null && "file_id" in val;
  }
  return true;
}

export function useMembershipCheckoutValidation(opts: {
  plan: Ref<MembershipPlanPublic | null>;
  quantity: Ref<number>;
  isFlexiblePricing: Ref<boolean>;
  flexiblePrice: Ref<number | null>;
  isAnyFileUploading: Ref<boolean>;
  hasMultiplePlans: Ref<boolean>;
  participants: Ref<ParticipantLocal[]>;
  customFields: Ref<MembershipCustomField[]>;
  getPlanCustomFields: (planId: string) => MembershipCustomField[];
  buyerInfo: Ref<{ firstName: string; lastName: string; email: string; confirmEmail: string }>;
}) {
  const canGoFromStep1 = computed(() => {
    if (opts.quantity.value < 1) return false;
    if (opts.isFlexiblePricing.value) {
      const v = Number(opts.flexiblePrice.value || 0);
      if (!v || v <= 0) return false;
      const min = opts.plan.value?.min_price != null ? Number(opts.plan.value.min_price) : null;
      const max = opts.plan.value?.max_price != null ? Number(opts.plan.value.max_price) : null;
      if (min !== null && v < min) return false;
      if (max !== null && v > max) return false;
    }
    return true;
  });

  const canGoFromStep1Single = computed(() => {
    if (opts.isAnyFileUploading.value) return false;
    if (opts.quantity.value === 1 && opts.participants.value.length === 1) {
      const p = opts.participants.value[0]!;
      const fields = opts.hasMultiplePlans.value ? opts.getPlanCustomFields(p.planId) : opts.customFields.value;
      for (const field of fields) {
        if (field.is_required) {
          const val = p.customFieldValues[field.id];
          if (!isCustomFieldValueValid(val, field.field_type)) return false;
        }
      }
    }
    return true;
  });

  const allParticipantsCustomFieldsValid = computed(() => {
    if (opts.isAnyFileUploading.value) return false;
    for (const p of opts.participants.value) {
      const fields = opts.hasMultiplePlans.value ? opts.getPlanCustomFields(p.planId) : opts.customFields.value;
      for (const field of fields) {
        if (field.is_required) {
          const val = p.customFieldValues[field.id];
          if (!isCustomFieldValueValid(val, field.field_type)) return false;
        }
      }
      if (!p.firstName || !p.lastName || !p.email) return false;
    }
    return true;
  });

  const coordsValid = computed(() => {
    return !!(opts.buyerInfo.value.firstName
      && opts.buyerInfo.value.lastName
      && opts.buyerInfo.value.email
      && opts.buyerInfo.value.confirmEmail
      && opts.buyerInfo.value.email === opts.buyerInfo.value.confirmEmail);
  });

  return { canGoFromStep1, canGoFromStep1Single, allParticipantsCustomFieldsValid, coordsValid };
}
